import { adminMetricsApi, type AdminGrowthMonth } from '@/api/admin/adminMetricsApi';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { currencyFormatter } from '@/utils/movements-utils';
import { handleGlobalError } from '@/utils/error-handler';
import { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

// Concrete Tailwind values, not a design-skill palette: this screen must be
// indistinguishable from the rest of the backoffice, not have its own identity.
// #2563eb is Tailwind's blue-600, already used as a class in AdminDeudaPage.tsx;
// #93c5fd is its blue-300. `<Cell fill>` needs a literal color and can't take a
// Tailwind class, so these are the one forced exception.
const SERIES_COLOR = '#2563eb';
const PARTIAL_COLOR = '#93c5fd';

interface SeriesSpec {
  key: 'tournaments' | 'activeClubs' | 'activePlayers' | 'fee';
  label: string;
  format: (value: number) => string;
}

// One chart per metric, each with its own axis. They do NOT share one chart on
// purpose: fee is money in the tens of thousands and the other three are counts
// in the tens, so a shared axis would flatten the counts into nothing.
const SERIES: SeriesSpec[] = [
  { key: 'tournaments', label: 'Torneos jugados', format: (v) => String(v) },
  { key: 'activeClubs', label: 'Clubes activos', format: (v) => String(v) },
  { key: 'activePlayers', label: 'Jugadores activos', format: (v) => String(v) },
  { key: 'fee', label: 'Fee facturado', format: (v) => currencyFormatter.format(v) },
];

// "2026-09" -> "sep 26". The axis has 12 ticks and the full label does not fit.
const shortMonth = (month: string) => {
  const [year, monthNumber] = month.split('-');
  const names = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${names[Number(monthNumber) - 1]} ${year.slice(2)}`;
};

const AdminMetricasPage = () => {
  const [months, setMonths] = useState<AdminGrowthMonth[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setHasFailed(false);
    try {
      setMonths(await adminMetricsApi.growth());
    } catch (error) {
      // This screen tracks the failure itself instead of leaning on the global
      // toast alone. On a chart an empty result and a failed request look
      // identical, and a zeroed chart reads as data, not as an error.
      setHasFailed(true);
      handleGlobalError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (hasFailed) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="text-2xl font-semibold text-gray-900">Métricas</h1>
        <div className="rounded-md border border-gray-200 py-10 text-center">
          <p className="text-sm text-gray-500">No se pudieron cargar las métricas.</p>
          <Button className="mt-4" onClick={load}>
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Métricas</h1>
        <p className="text-sm text-gray-500">
          Actividad de los últimos 12 meses. El mes en curso está incompleto.
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-56" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            {SERIES.map((series) => (
              <div key={series.key} className="rounded-md border border-gray-200 p-4">
                <h2 className="mb-3 text-sm font-medium text-gray-700">{series.label}</h2>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={months}>
                    <XAxis
                      dataKey="month"
                      tickFormatter={shortMonth}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis tick={{ fontSize: 11 }} width={48} />
                    <Tooltip
                      formatter={(value: number) => series.format(value)}
                      labelFormatter={shortMonth}
                    />
                    <Bar dataKey={series.key}>
                      {months.map((month) => (
                        // The month in progress is drawn faded: without this the
                        // last bar is always half-full and the chart tells the
                        // same lie every month, that the business collapsed.
                        <Cell
                          key={month.month}
                          fill={month.partial ? PARTIAL_COLOR : SERIES_COLOR}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mes</TableHead>
                <TableHead className="text-right">Torneos</TableHead>
                <TableHead className="text-right">Clubes</TableHead>
                <TableHead className="text-right">Jugadores</TableHead>
                <TableHead className="text-right">Fee</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {months.map((month) => (
                <TableRow key={month.month}>
                  <TableCell>
                    {month.month}
                    {month.partial ? (
                      <span className="ml-2 text-xs text-gray-500">(en curso)</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right">{month.tournaments}</TableCell>
                  <TableCell className="text-right">{month.activeClubs}</TableCell>
                  <TableCell className="text-right">{month.activePlayers}</TableCell>
                  <TableCell className="text-right">
                    {currencyFormatter.format(month.fee)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
};

export default AdminMetricasPage;
